import React, { useState, useRef } from "react";
import {
  Button,
  CardActions,
  CardContent,
  Grid,
  Avatar,
  TextField,
  Box,
  Typography,
} from "@mui/material";

import { updatePlannerOrganizationData } from "../../../api/providers/plannerOrganizationProviders";
import { updateOrganizationLogo } from "../../../api/providers/plannerEventsProviders";
import { OrganizationInterface } from "../../../interfaces";
import CustomModal from "../../../components/public/CustomModal";

interface OrganizationData {
  organizationData: OrganizationInterface | null;
  setOrganizationData: React.Dispatch<
    React.SetStateAction<OrganizationInterface | null>
  >;
  getOrganizationData: () => void;
}

export const ProfileDataView = ({
  organizationData,
  setOrganizationData,
  getOrganizationData,
}: OrganizationData) => {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(organizationData?.name || "");
  const [email, setEmail] = useState(organizationData?.email || "");
  const [phoneNumber, setPhoneNumber] = useState(
    organizationData?.phoneNumber || ""
  );
  const [instagram, setInstagram] = useState(organizationData?.instagram || "");
  const [linkedin, setLinkedin] = useState(organizationData?.linkedin || "");
  const [twitter, setTwitter] = useState(organizationData?.twitter || "");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  type UpdatedFields = {
    name?: string;
    email?: string;
    password?: string;
    phoneNumber?: string;
    instagram?: string;
    twitter?: string;
    linkedin?: string;
  };

  const updateData = async () => {
    try {
      const updatedFields: UpdatedFields = {};

      if (name !== organizationData?.name) {
        updatedFields.name = name;
      }
      if (email !== organizationData?.email) {
        updatedFields.email = email;
      }
      if (phoneNumber !== "" && phoneNumber !== organizationData?.phoneNumber) {
        updatedFields.phoneNumber = phoneNumber;
      }
      if (instagram !== organizationData?.instagram) {
        updatedFields.instagram = instagram;
      }
      if (linkedin !== organizationData?.linkedin) {
        updatedFields.linkedin = linkedin;
      }
      if (twitter !== organizationData?.twitter) {
        updatedFields.twitter = twitter;
      }

      if (Object.keys(updatedFields).length === 0) {
        console.log("No hay cambios para actualizar");
        return;
      }

      const result = await updatePlannerOrganizationData(updatedFields);
      if (result.ok) {
        setShowSuccessModal(true);
        setOrganizationData(result.data);
        setIsEditing(false);
      }
    } catch (error) {
      console.error("Error obteniendo los datos del usuario:", error);
    }
  };

  const toggleEdit = () => {
    if (isEditing) updateData();
    else setIsEditing(!isEditing);
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await updateOrganizationLogo(formData);
        if (response.ok) {
          getOrganizationData();
        }
      } catch (error) {
        console.error("Error al actualizar el logo:", error);
      }
    }
  };

  return (
    <Grid container spacing={2} p={4}>
      <Box>
        <Typography
          fontSize={32}
         
          component="div"
          align="left"
        >
          Información de organizador
        </Typography>
      </Box>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            mb: 0,
          }}
        >
          <Avatar
            alt="Organization Logo"
            src={organizationData?.logo}
            sx={{ width: 100, height: 100 }}
          />
          <Button variant="text" color="warning" onClick={handleButtonClick}  sx={{ textTransform: "none" }}>
            Cambiar imagen
          </Button>
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            ref={fileInputRef}
            onChange={handleFileChange}
          />
        </Box>
        <Grid container spacing={2} >
          <Grid item xs={12} sm={6}>
            <TextField
              label="Nombre"
              variant={isEditing ? "filled" : "outlined"}
              fullWidth
              color="primary"
              margin="normal"
              value={name}
              onChange={(e) => setName(e.target.value)}
              InputProps={{
                readOnly: !isEditing,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Instagram"
              variant={isEditing ? "filled" : "outlined"}
              fullWidth
              color="primary"
              margin="normal"
              value={instagram}
              onChange={(e) => setInstagram(e.target.value)}
              InputProps={{
                readOnly: !isEditing,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Correo electrónico"
              variant={isEditing ? "filled" : "outlined"}
              fullWidth
              color="primary"
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                readOnly: !isEditing,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Linkedin"
              variant={isEditing ? "filled" : "outlined"}
              fullWidth
              color="primary"
              margin="normal"
              value={linkedin}
              onChange={(e) => setLinkedin(e.target.value)}
              InputProps={{
                readOnly: !isEditing,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Teléfono"
              variant={isEditing ? "filled" : "outlined"}
              fullWidth
              color="primary"
              margin="normal"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              InputProps={{
                readOnly: !isEditing,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Twitter"
              variant={isEditing ? "filled" : "outlined"}
              fullWidth
              color="primary"
              margin="normal"
              value={twitter}
              onChange={(e) => setTwitter(e.target.value)}
              InputProps={{
                readOnly: !isEditing,
              }}
            />
          </Grid>
        </Grid>
      </CardContent>
      <CardActions
        disableSpacing
        sx={{ flexDirection: "column", alignItems: "flex-start" }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={toggleEdit}
          
          sx={{  marginTop: 1, marginLeft: 1 }}
        >
          {isEditing ? "Guardar" : "Editar"}
        </Button>
      </CardActions>
      <CustomModal
        title={"Información actualizada con éxito"}
        onPress={handleCloseModal}
        isVisible={showSuccessModal}
      />
    </Grid>
  );
};
